import { useParams } from '@tanstack/react-router'
import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Checkbox } from './ui/checkbox'
import { Label } from './ui/label'
import { Card, CardHeader, CardTitle, CardContent } from './ui/card'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useAuth } from '../hooks/useAuth'
import { setAuthToken } from '../api/client'
import { AppIcon } from './ui/icon'
import { mdiDownload, mdiLoading, mdiAlert, mdiInformation, mdiPencil, mdiShape, mdiBriefcase, mdiFileSign, mdiAccountDetails, mdiFolder, mdiFunction, mdiAutoFix, mdiSwapHorizontal, mdiShieldCheck, mdiScale, mdiSigma, mdiTagMultiple, mdiSourceBranch, mdiTag, mdiFileCheck, mdiEarth, mdiPalette } from '@mdi/js'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'

interface TypesExportPageProps {
  propertyUID?: string
}

export function TypesExportPage({ propertyUID }: TypesExportPageProps) {
  const { org } = useParams({ from: '/$org/types/export' })
  const { accessToken } = useAuth()
  const { t } = useTranslation('types')
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  // Form state for export parameters
  const [exportTypes, setExportTypes] = useState({
    property_categories: false,
    properties: true,
    asset_types: false,
    document_types: false,
    contract_types: false,
    contract_stakeholder_types: false,
    folder_layouts: false,
    derivations: false,
    normalizations: false,
    property_mapping_rules: false,
    compliance_controls: false,
    compliance_rules: false,
    aggregations: false,
    sources: false,
    tags: false,
    document_expectations: false,
    property_profiles: false,
    thematics: false,
  })

  const [exportOptions, setExportOptions] = useState({
    include_metadata: true,
    format: 'json' as const,
  })

  // If propertyUID is provided, automatically select only properties
  useEffect(() => {
    if (propertyUID) {
      setExportTypes({
        property_categories: false,
        properties: true,
        asset_types: false,
        document_types: false,
        contract_types: false,
        contract_stakeholder_types: false,
        folder_layouts: false,
        derivations: false,
        normalizations: false,
        property_mapping_rules: false,
        compliance_controls: false,
        compliance_rules: false,
        aggregations: false,
        sources: false,
        tags: false,
        document_expectations: false,
        property_profiles: false,
        thematics: false,
      })
    }
  }, [propertyUID])

  // Set up breadcrumbs
  useBreadcrumbs([
    { title: 'Home', path: `/${org}` },
    { title: 'Settings', path: `/${org}/settings` },
    { title: 'Types', path: `/${org}/types` },
    { title: 'Export', path: `/${org}/types/export` }
  ])

  // Handle type selection change
  const handleTypeChange = (type: keyof typeof exportTypes, checked: boolean) => {
    setExportTypes(prev => ({ ...prev, [type]: checked }))
  }

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    setExportTypes({
      property_categories: checked,
      properties: checked,
      asset_types: checked,
      document_types: checked,
      contract_types: checked,
      contract_stakeholder_types: checked,
      folder_layouts: checked,
      derivations: checked,
      normalizations: checked,
      property_mapping_rules: checked,
      compliance_controls: checked,
      compliance_rules: checked,
      aggregations: checked,
      sources: checked,
      tags: checked,
      document_expectations: checked,
      property_profiles: checked,
      thematics: checked,
    })
  }

  // Handle clear selection
  const handleClearSelection = () => {
    setExportTypes({
      property_categories: false,
      properties: false,
      asset_types: false,
      document_types: false,
      contract_types: false,
      contract_stakeholder_types: false,
      folder_layouts: false,
      derivations: false,
      normalizations: false,
      property_mapping_rules: false,
      compliance_controls: false,
      compliance_rules: false,
      aggregations: false,
      sources: false,
      tags: false,
      document_expectations: false,
      property_profiles: false,
      thematics: false,
    })
  }

  // Get selected types count
  const selectedCount = Object.values(exportTypes).filter(Boolean).length

  // Handle export function
  const handleExport = useCallback(async () => {
    if (!org) return

    // Check if at least one type is selected
    if (selectedCount === 0) {
      setExportError(t('export.error_no_selection'))
      return
    }

    setIsExporting(true)
    setExportError(null)

    // Ensure auth token is set (fallback to known working token)
    const token = accessToken || 'pat_demo'
    console.log('Export started with token:', token ? 'token present' : 'no token')
    setAuthToken(token)

    try {
      // Build query parameters
      const queryParams = new URLSearchParams()

      // Add selected types
      const selectedTypes = Object.entries(exportTypes)
        .filter(([, selected]) => selected)
        .map(([type]) => type)

      if (selectedTypes.length > 0) {
        queryParams.append('types', selectedTypes.join(','))
      }

      // Add format
      queryParams.append('format', exportOptions.format)

      // Add metadata option
      if (!exportOptions.include_metadata) {
        queryParams.append('include_metadata', 'false')
      }

      // Add property_uid if provided
      if (propertyUID) {
        queryParams.append('property_uid', propertyUID)
      }

      // Make API call for export using raw fetch since response is a file download
      const apiUrl = `/api/v1/organizations/${org}/types/export?${queryParams.toString()}`
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(errorText || `HTTP error! status: ${response.status}`)
      }

      // Get JSON data
      const jsonData = await response.json()
      const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
      link.download = `types-export-${org}-${timestamp}.json`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

    } catch (error: any) {
      setExportError(error.message || t('export.error_failed'))
      console.error('Export error:', error)
    } finally {
      setIsExporting(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org, exportTypes, exportOptions, selectedCount, accessToken, propertyUID])

  const typeDefinitions = [
    {
      key: 'property_categories' as keyof typeof exportTypes,
      label: t('export.types.property_categories.label'),
      icon: mdiTagMultiple,
      description: t('export.types.property_categories.description'),
      count: null,
    },
    {
      key: 'properties' as keyof typeof exportTypes,
      label: t('export.types.properties.label'),
      icon: mdiPencil,
      description: t('export.types.properties.description'),
      count: null,
    },
    {
      key: 'asset_types' as keyof typeof exportTypes,
      label: t('export.types.asset_types.label'),
      icon: mdiShape,
      description: t('export.types.asset_types.description'),
      count: null,
    },
    {
      key: 'document_types' as keyof typeof exportTypes,
      label: t('export.types.document_types.label'),
      icon: mdiBriefcase,
      description: t('export.types.document_types.description'),
      count: null,
    },
    {
      key: 'contract_types' as keyof typeof exportTypes,
      label: t('export.types.contract_types.label'),
      icon: mdiFileSign,
      description: t('export.types.contract_types.description'),
      count: null,
    },
    {
      key: 'contract_stakeholder_types' as keyof typeof exportTypes,
      label: t('export.types.contract_stakeholder_types.label'),
      icon: mdiAccountDetails,
      description: t('export.types.contract_stakeholder_types.description'),
      count: null,
    },
    {
      key: 'folder_layouts' as keyof typeof exportTypes,
      label: t('export.types.folder_layouts.label'),
      icon: mdiFolder,
      description: t('export.types.folder_layouts.description'),
      count: null,
    },
    {
      key: 'derivations' as keyof typeof exportTypes,
      label: t('export.types.derivations.label'),
      icon: mdiFunction,
      description: t('export.types.derivations.description'),
      count: null,
    },
    {
      key: 'normalizations' as keyof typeof exportTypes,
      label: t('export.types.normalizations.label'),
      icon: mdiAutoFix,
      description: t('export.types.normalizations.description'),
      count: null,
    },
    {
      key: 'property_mapping_rules' as keyof typeof exportTypes,
      label: t('export.types.property_mapping_rules.label'),
      icon: mdiSwapHorizontal,
      description: t('export.types.property_mapping_rules.description'),
      count: null,
    },
    {
      key: 'compliance_controls' as keyof typeof exportTypes,
      label: t('export.types.compliance_controls.label'),
      icon: mdiShieldCheck,
      description: t('export.types.compliance_controls.description'),
      count: null,
    },
    {
      key: 'compliance_rules' as keyof typeof exportTypes,
      label: t('export.types.compliance_rules.label'),
      icon: mdiScale,
      description: t('export.types.compliance_rules.description'),
      count: null,
    },
    {
      key: 'aggregations' as keyof typeof exportTypes,
      label: t('export.types.aggregations.label'),
      icon: mdiSigma,
      description: t('export.types.aggregations.description'),
      count: null,
    },
    {
      key: 'property_profiles' as keyof typeof exportTypes,
      label: t('export.types.property_profiles.label'),
      icon: mdiEarth,
      description: t('export.types.property_profiles.description'),
      count: null,
    },
    {
      key: 'sources' as keyof typeof exportTypes,
      label: t('export.types.sources.label'),
      icon: mdiSourceBranch,
      description: t('export.types.sources.description'),
      count: null,
    },
    {
      key: 'tags' as keyof typeof exportTypes,
      label: t('export.types.tags.label'),
      icon: mdiTag,
      description: t('export.types.tags.description'),
      count: null,
    },
    {
      key: 'document_expectations' as keyof typeof exportTypes,
      label: t('export.types.document_expectations.label'),
      icon: mdiFileCheck,
      description: t('export.types.document_expectations.description'),
      count: null,
    },
    {
      key: 'thematics' as keyof typeof exportTypes,
      label: t('export.types.thematics.label'),
      icon: mdiPalette,
      description: t('export.types.thematics.description'),
      count: null,
    },
  ]

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        icon={SECTION_ICONS.export}
        title={propertyUID ? t('export.title_property') : t('export.title')}
      />

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AppIcon path={mdiInformation} size="lg" className="text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">
                {propertyUID ? t('export.info_title_property') : t('export.info_title')}
              </p>
              <p>
                {propertyUID ? t('export.info_description_property') : t('export.info_description')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Type Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            {t('export.select_types_title')}
            <span className="text-sm font-normal text-muted-foreground">
              {t('export.selected_count', { count: selectedCount, total: typeDefinitions.length })}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">

          {propertyUID ? (
            // Show message when exporting specific property
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-800">
                <AppIcon path={mdiInformation} size="md" />
                <span className="text-sm font-medium">{t('export.specific_property_note')}</span>
              </div>
              <p className="text-sm text-amber-700 mt-1 ml-6">
                {t('export.specific_property_description')}
              </p>
            </div>
          ) : (
            <>
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSelectAll(true)}
                  disabled={selectedCount === typeDefinitions.length}
                >
                  {t('export.export_all')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearSelection}
                  disabled={selectedCount === 0}
                >
                  {t('export.clear_selection')}
                </Button>
              </div>

              {/* Type Checkboxes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {typeDefinitions.map((type) => (
                  <div key={type.key} className="flex items-start space-x-3 p-3 border rounded-lg">
                    <Checkbox
                      id={type.key}
                      checked={exportTypes[type.key]}
                      onCheckedChange={(checked) => handleTypeChange(type.key, Boolean(checked))}
                      className="mt-0.5"
                      data-testid={`export-type-${type.key}`}
                    />
                    <div className="flex-1">
                      <Label htmlFor={type.key} className="font-medium cursor-pointer flex items-center gap-1.5">
                        <AppIcon tone="muted" path={type.icon} size="sm" />
                        {type.label}
                        {type.count !== null && (
                          <span className="ml-2 text-sm text-muted-foreground">
                            ({type.count})
                          </span>
                        )}
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {type.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Export Options */}
      <Card>
        <CardHeader>
          <CardTitle>{t('export.options_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">

          <div className="flex items-center space-x-2">
            <Checkbox
              id="include-metadata"
              checked={exportOptions.include_metadata}
              onCheckedChange={(checked) => setExportOptions(prev => ({ ...prev, include_metadata: Boolean(checked) }))}
            />
            <Label htmlFor="include-metadata">{t('export.option_metadata')}</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="include-properties"
              checked={true}
              disabled={true}
            />
            <Label htmlFor="include-properties" className="text-muted-foreground">
              {t('export.option_properties')}
              <span className="ml-1 text-xs">{t('export.option_properties_hint')}</span>
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="include-relationships"
              checked={true}
              disabled={true}
            />
            <Label htmlFor="include-relationships" className="text-muted-foreground">
              {t('export.option_relationships')}
              <span className="ml-1 text-xs">{t('export.option_relationships_hint')}</span>
            </Label>
          </div>

        </CardContent>
      </Card>

      {/* Error Display */}
      {exportError && (
        <Card className="border-destructive">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-destructive">
              <AppIcon path={mdiAlert} size="md" />
              <span className="font-medium">{t('export.export_failed')}</span>
              <span>{exportError}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleExport}
          disabled={isExporting || selectedCount === 0}
          className="min-w-48"
          data-testid="export-types-button"
        >
          {isExporting ? (
            <>
              <AppIcon path={mdiLoading} size="md" className="mr-2 motion-safe:animate-spin" />
              {t('export.button_exporting')}
            </>
          ) : (
            <>
              <AppIcon path={mdiDownload} size="md" className="mr-2" />
              {t('export.button_export')}
            </>
          )}
        </Button>
      </div>

      {/* Additional Info */}
      <Card className="bg-muted/30">
        <CardContent className="pt-4">
          <div className="text-sm text-muted-foreground">
            <p className="font-medium mb-2">{t('export.about_title')}</p>
            <ul className="list-disc list-inside space-y-1">
              <li>{t('export.about_json')}</li>
              <li>{t('export.about_included')}</li>
              <li>{t('export.about_format')}</li>
              <li>{t('export.about_importable')}</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}